import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Button from '@material-ui/core/Button';
import Menu from '@material-ui/core/Menu';
import Divider from '@material-ui/core/Divider';
import PersonIcon from '@material-ui/icons/Person';
import RoleIcon from '@material-ui/icons/SupervisedUserCircle';
import TourIcon from '@material-ui/icons/Slideshow';
import KeyIcon from '@material-ui/icons/Code';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import EmailIcon from '@material-ui/icons/Email';
import LogoutIcon from '@material-ui/icons/ExitToApp';
import UserMenuItem from './UserMenuItem';
import MenuLink from './MenuLink';
import './UserBar.scss';

const DEFAULT_MENU_ITEMS = {
  email: {
    id: 'userEmailMenuItem',
    icon: EmailIcon,
    label: (props) => props.authService.getEmail(),
    condition: (props) => props.authService.getEmail(),
    disabled: true,
  },
  divider: {
    divider: true,
  },
  role: {
    id: 'userRoleMenuItem',
    icon: RoleIcon,
    label: (props) => `Logged in as ${props.authService.getRole()}`,
    condition: (props) => {
      const role = props.authService.getRole();
      const defaultRole = props.authService.getDefaultRole ? props.authService.getDefaultRole() : 'STANDARD';
      return role && (role !== defaultRole);
    },
    disabled: true,
  },
  takeTour: {
    id: 'takeTourMenuItem',
    icon: TourIcon,
    label: 'Take a tour',
    onClick: 'onTakeTour',
  },
  profile: {
    id: 'editProfileMenuItem',
    icon: PersonIcon,
    label: 'Profile',
    onClick: 'onEditProfile',
  },
  keys: {
    id: 'editKeysMenuItem',
    icon: KeyIcon,
    label: 'API Keys',
    onClick: 'onEditKeys',
  },
  logout: {
    id: 'logoutMenuItem',
    icon: LogoutIcon,
    label: 'Logout',
    onClick: ({ authService }) => {
      authService.logout();
    },
  },
};

const DEFAULT_MENU = [
  'email',
  'role',
  'divider',
  'takeTour',
  'profile',
  'keys',
  'divider',
  'logout',
];

class UserBar extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      anchorEl: null,
    };
    this.handleUserClick = this.handleUserClick.bind(this);
    this.handleUserClose = this.handleUserClose.bind(this);

    const { viewService } = props;
    const menuDesc = this.props.userBarMode && this.props.userBarMode.menu || DEFAULT_MENU;
    const makeItemRenderer = (desc) => {
      const d = typeof desc === 'string' ? DEFAULT_MENU_ITEMS[desc] : desc;
      if (!d) {
        console.error('No menu item definition for ', desc);
        return null;
      }
      if (d.divider) {
        return {
          id: d.id,
          condition: d.condition,
          divider: true,
          Component: Divider,
        };
      }
      if (d.render) {
        return d;
      }
      const { onClick, label, icon, href, condition, ...rest } = d;
      let cond = condition;
      const Icon = icon && (typeof icon === 'string') ? viewService.getIcon(icon) : icon;
      const labelFct = typeof label === 'string' ? () => label : label;
      const hrefFct = href && (typeof href === 'string') ? () => href : href;
      let click = onClick;
      if (onClick) {
        if (typeof onClick === 'string') {
          // onClick corresponds to method provided as a prop
          click = () => {
            const fct = this.props[d.onClick];
            if (fct) {
              fct(this.props);
            }
          };
          if (!cond) {
            cond = pps => !!pps[d.onClick];
          }
        } else {
          click = () => {
            d.onClick(this.props);
          };
        }
      }
      return {
        condition: cond,
        computeProperties: d.computeProperties || (pps => ({
          ...rest,
          label: labelFct(pps),
          Icon,
          disabled: !d.disabled || (d.disabled === true) ? d.disabled : d.disabled(pps),
          onClick: click,
          href: hrefFct && hrefFct(pps) || undefined,
        })),
        Component: hrefFct ? MenuLink : UserMenuItem,
      };
    };
    this.menuItems = menuDesc.map(makeItemRenderer);
  }

  handleUserClick(event) {
    const { anchorEl } = this.state;
    this.setState({ anchorEl: anchorEl ? null : event.currentTarget });
  }

  handleUserClose() {
    this.setState({ anchorEl: null });
  }

  render() {
    const { username, userBarMode, children } = this.props;
    const { anchorEl } = this.state;
    let sectionEmpty = true; // Used to determine whether a divider can display (section empty === false)
    let dividerCount = 0;

    return (
      <div className={classnames('grb-user-bar', userBarMode.className)}>
        {children}
        <Button
          aria-owns={anchorEl ? 'simple-menu' : undefined}
          aria-haspopup="true"
          onClick={this.handleUserClick}
          id="userMenuButton"
          className={userBarMode.selectClassname}
        >
          <PersonIcon className="grb-user-icon" />
          <span className={classnames('grb-username', userBarMode.usernameClassname)}>{username || ''}</span>
          <ArrowDropDownIcon />
        </Button>
        <Menu
          id={userBarMode.menuId || 'userProfileMenu'}
          anchorEl={anchorEl}
          // https://github.com/mui-org/material-ui/issues/7961#issuecomment-326116559
          getContentAnchorEl={null}
          open={Boolean(anchorEl)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          onClose={this.handleUserClose}
          onClick={this.handleUserClose}
          className={classnames('grb-user-bar-menu', userBarMode.menuClassname)}
        >
          {this.menuItems.map((mi) => {
            if (mi.condition && !mi.condition(this.props)) {
              return undefined;
            }
            if (mi.divider) {
              const show = !sectionEmpty;
              sectionEmpty = true;
              dividerCount += 1;
              const id = mi.id || `divider-${dividerCount}`;
              return show && <mi.Component id={id} key={id} />;
            }
            sectionEmpty = false;
            const miProps = mi.computeProperties(this.props);
            return <mi.Component {...miProps} key={miProps.id} />;
          }).filter(e => e)}
        </Menu>
      </div>
    );
  }
}

UserBar.propTypes = {
  username: PropTypes.string,
  authService: PropTypes.shape({
    logout: PropTypes.func.isRequired,
    getEmail: PropTypes.func.isRequired,
    getRole: PropTypes.func.isRequired,
  }).isRequired,
  viewService: PropTypes.shape({
    getComponent: PropTypes.func,
    getIcon: PropTypes.func,
  }).isRequired,
  userBarMode: PropTypes.shape({
    menu: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({}),
      ]),
    ),
    className: PropTypes.string,
    menuId: PropTypes.string,
    menuClassname: PropTypes.string,
    usernameClassname: PropTypes.string,
    selectClassname: PropTypes.string,
  }),
  children: PropTypes.node,
};

UserBar.defaultProps = {
  username: undefined,
  userBarMode: undefined,
  children: undefined,
};

export default (userBarMode = {}) => {
  return props => <UserBar {...props} userBarMode={userBarMode} />;
};
